import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { VideoEnvironmentProps } from '../../cvp-webgames-core/Components/Environments/React/VideoEnvironment';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { SuccessType } from '../general/general-scenario-stuff';
import { Windows } from '../../misc/tools';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';

export default class GuadalupeHerrera extends BaseTestScenario {
	public bVMCorrect: boolean;
	public addTextB: boolean = false;
	public nasalOxy: boolean = false;

	public get maxScore(): number {
		return 52300;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 0; // patient has dentures
		var setupFiberOpticsPoints = 1000;
		var cricPoints = 1900;
		var correctAttempt = 32700;

		return assessPatientRemoveDenturesPoints +
			setupFiberOpticsPoints +
			cricPoints +
			correctAttempt;
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 6;
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30, //set at 3 for testing, change to 30
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 95,
			bloodPressureDenominator: 60,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 85,
			heartRate: 125,
			heartRateMax: 150,
			respirationRate: 32,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time to save Ms. Herrera\’s life. She has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'Ms. Herrera has severe kyphosis and has likely anatomy distortion, which makes her difficult to intubate. As a result, it is critical that you call for a flexible bronchoscope early.',
				'In a kyphotic patient, an NPA is more likely than an OPA to establish a patent airway. An OPA will be difficult to insert due to the anatomy disruption from the kyphosis.',
				'In kyphotic patients, the carotid arteries are very anterior. As a result, any attempt at a cricothyrotomy will result in a carotid artery laceration. Additionally, your likelihood of achieving a success cricothyrotomy is remote due to the inaccessibility of the cricothyroid membrane.',
				'Due to the patient\s rapid respiratory decompensation you were forced to actively manage her airway while waiting for the bronchoscope to arrive. If the patient\'s respiratory status had been stable, then waiting for the flexible bronchoscope to arrive would have been the optimal management strategy for this patient.',
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		if(this.bVMCorrect) {
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, '', ''); // Must use OPA or NPA, or is a failed attempt
			
		} else {
			this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
		}
	}	

	public onETTFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished(): void {
		
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onCricFinished(): void {
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.Cric, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}	
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		const context = this;
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'The patient’s larynx is obstructed due to severe kyphosis, but it’s mobile and midline.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'I can’t lift my chin.'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'My chin is already touching my chest.'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patients dentures have been removed.',
				onClick: () => {
					context.performActionWithWarning('Warning', 'This is difficult to do because of patient\'s kyphosis, so it takes longer to execute.');
				}
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'No, they’re dentures.'				
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				imgUrl: './assets/images/assess-airway-images/Class4SmallOpenMouth.png',
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 2 finger breadths (3 cm)'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Guadalupe Herrera. She’s an 86-year-old female, 
		// 			62 in, 50 kg. She suffers from pulmonary fibrosis, kyphosis, 
		// 			and severe osteoporosis. She’s currently in respiratory distress 
		// 			with likely pneumonia. Her K is 3.2. She has not received her 
		// 			breakfast tray yet.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario5 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			Due to this woman’s comorbidities and her likely pneumonia, we’re 
		// 			requesting she be intubated for therapeutic management.					
		// 			I’m already administering epinephrine and steroids. I’ll manage the
		// 				hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario5 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Guadalupe Herrera is currently in respiratory distress with likely pneumonia. Her K is 3.2. She has not received her breakfast tray yet.`,
		// 		b: `
		// 			Age: 86<br />
		// 			Height (in): 62 in<br />
		// 			Weight (kg): 50 kg<br />
		// 			Potassium (K): 3.2<br />
		// 			NPO Status: Yes NPO<br />
		// 			<br />
		// 			PMH: Guadalupe Herrera has pulmonary fibrosis, kyphosis, and severe osteoporosis.
		// 		`,
		// 		a: `This patient has likely pneumonia.`,
		// 		r: `Due to this woman’s comorbidities and her likely pneumonia, we’re requesting she be intubated for therapeutic management.`
		// 	}
		// }

		this._id_ScenarioSet(5); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario5 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Guadalupe Herrera is currently in respiratory distress with likely pneumonia. Her K is 3.2. She has not received her breakfast tray yet.`,
				b: `
					Age: 86<br />
					Height (in): 62 in<br />
					Weight (kg): 50 kg<br />
					Potassium (K): 3.2<br />
					NPO Status: Yes NPO<br />
					<br />
					PMH: Guadalupe Herrera has pulmonary fibrosis, kyphosis, and severe osteoporosis.
				`,
				b508: `
					Age. 86.
					Height. 62 inches.
					Weight. 50 kilograms.
					Potassium. 3.2.
					NPO Status. Yes NPO.
				
					Past Medical History. Guadalupe Herrera has pulmonary fibrosis kyphosis and severe osteoporosis.
				`,
				a: `This patient has likely pneumonia.`,
				r: `Due to this woman's comorbidities and her likely pneumonia, we're requesting she be intubated for therapeutic management.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario5 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader');
			},
			isCric: false
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error. ")
			},
			onTeamDebriefed: () => {
				this.debriefLog('Briefed other staff in the room about attempt order');
			},
			onToolsOrganized: () => {
				this.debriefLog('Organized equipment');
			}
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario5 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction set");
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario5 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario5 - 45 - patient-assessment-down - 37',
			chinToChestVideoChapter: () =>'DAARC - Scenario5 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario5 - 45 - patient-assessment-down - 37',
			realTeethVideoChapter: () =>'DAARC - Scenario5 - 45 - patient-assessment-down - 37',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario5 - 45 - patient-assessment-down - 37',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for flexible bronchoscope")
			}			
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // this needs to update based on the selection made		
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						this.nasalOxy = true;
						break;
					case 1:
						this.nasalOxy = true;	
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.");
				//this.debriefLog('By giving her neuromuscular junction blockers, you were able to reduce pharyngeal tone and thereby optimize bag valve mask ventilation.')
				this.addText = ' By giving her neuromuscular junction blockers, you were able to reduce pharyngeal tone and thereby optimize bag valve mask ventilation.'
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	public getDefaultSa02() {
		return 85
	}

	public getDefaultApneaTime() {
		return 210;
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	private _setupBvm() {
		this.procedures.bvm.config.isAttemptSuccessful = () => {
			return this.bVMCorrect;
		}

		this.procedures.bvm.config.onMakeAttemptClicked = (isOneHanded: boolean, isOPA: boolean, isNPA: boolean, isManipulateHeadAndNeck: boolean) => {
			if(isOPA || isNPA) {					
				this.bVMCorrect = true;
			}
			else{
				this.bVMCorrect = false;
			}
		}

		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			onMakeAttemptClicked: (isNPAClicked: boolean, isOPAClicked: boolean) => {
				if(isNPAClicked || isOPAClicked) {					
					this.bVMCorrect = true;
				}
				else{
					this.bVMCorrect = false;
				}
			},
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.ettResistanceConfig = { 
			show: () => true,
			onNextClicked: () => {
			}
		}

		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade4.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount
			}
		};
	}

	private _setupCric() {
		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			]
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),
			areLandmarksObstructed: true,
			landmarksObstructedMessageTitle: 'Landmark Identification Obstructed'
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat'),
			onMakingIncition: () => {
				this._CheckOxy();
				this.cricAttemptCount++;
				this.showFailedScenarioPopUp("You attempted to make an incision when you could not identify any anatomical landmarks. As a result, Ms. Herrera expired. Review the debrief and then try again.")
			}	
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat')
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')
		};
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario5/daarc-scenario5-guadalupeherrera-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				checkRiseAndTubeHumidificationText: () => {
					if(this.bVMCorrect) {
						return this.auscSuccess
					} else {
						return this.auscFailed
					}
				},
				audioPath: () => {
					if(this.bVMCorrect) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else {
						return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
					}
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.bVMCorrect) {
						return isYesClicked
					} else return !isYesClicked
				},
				onBeforeDebriefShows: (onDone: () => void) => {
					const context = this;
					if(context.nasalOxy) {
						context.uiWrapper.displayGeneralAlertUI({ 
							title: 'Oxygenation Method',
							text: 'Nasal Oxygenation was providing equivalent oxygenation to bag valve mask. Do you wish to switch back to that device? ',
							okButton: {
								title: 'Yes',
								onClicked: () => {
									context.addTextB = true;
									this.attemptTitle = 'Good Job on this Scenario';
									this.attemptText = 'Good, you remembered to swap back to nasal oxygenation! ' + this.addText + 'You were able to get Ms. Herrera into the green zone using either an OPA or NPA and then ventilating via the bag valve mask.  Ms. Herrera was apneic for ' + this.apneaTimeElapsed + ' while you managed her airway. Due to the patient\'s rapid respiratory decomposition you were forced to avtively manage her airway while waiting for the flexible bronchoscope to arrive. You kept her in the green zone until the team with the flexible bronchoscope arrived. The team then established a stable, patent airway for her. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points. Remember  that you administered a paralytic drug and she will require supportive  ventilation until the advanced  airway cart arrives with video bronchoscopy.';
									onDone();
								}
							},
							cancelbutton: {
								title: 'No',
								onClicked: () => {
									context.addTextB = false;
									this.attemptTitle = 'Good Job on this Scenario';
									this.attemptText = 'The Nasal oxygenation pre-oxy option was better than manually doing bvm. ' + this.addText + 'You were able to get Ms. Herrera into the green zone using either an OPA or NPA and then ventilating via the bag valve mask.  Ms. Herrera was apneic for ' + this.apneaTimeElapsed + ' while you managed her airway. Due to the patient\'s rapid respiratory decomposition you were forced to avtively manage her airway while waiting for the flexible bronchoscope to arrive. You kept her in the green zone until the team with the flexible bronchoscope arrived. The team then established a stable, patent airway for her. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points. Remember  that you administered a paralytic drug and she will require supportive  ventilation until the advanced  airway cart arrives with video bronchoscopy.';
									
									context.debriefLogFailedItem('The Nasal oxygenation pre-oxy option was better than manually doing bvm.');
									onDone();
								}
							}
						});
					} else {
						this.attemptTitle = 'Good Job on this Scenario';
						this.attemptText = this.addText + 'You were able to get Ms. Herrera into the green zone using either an OPA or NPA and then ventilating via the bag valve mask.  Ms. Herrera was apneic for ' + this.apneaTimeElapsed + ' while you managed her airway. Due to the patient\'s rapid respiratory decomposition you were forced to avtively manage her airway while waiting for the flexible bronchoscope to arrive. You kept her in the green zone until the team with the flexible bronchoscope arrived. The team then established a stable, patent airway for her. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points. Remember  that you administered a paralytic drug and she will require supportive  ventilation until the advanced  airway cart arrives with video bronchoscopy.';
						onDone();
					}
				},
				auscultateHoverOverMessages: () => {
					if(this.bVMCorrect) {
						return this.hoverBreathYes
					} else {
						return this.hoverBreathNo
					}
				}
			},
			ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo;
				}
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo;
				}
			},
			cricConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes;
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}
}